<template>
  <el-card v-loading="loading">
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Users' }">{{String($root.state.facilityData.code).toUpperCase()}} Users</el-breadcrumb-item>
        <el-breadcrumb-item v-else :to="{ name: 'Users' }">Users</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: 'User Form' }">New User</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <el-form :model="form" ref="form">
      <el-form-item label="Title" prop="title" :rules="[{
        required: true,
        message: 'Please input title', trigger: 'blur'
       }]">
        <el-input v-model="form.title"></el-input>
      </el-form-item>
      <el-form-item label="Email" prop="email" :rules="[{
        required: true,
        message: 'Please input email address', trigger: 'blur'
      }, { type: 'email', message: 'Please input correct email address',
            trigger: ['blur', 'change'] }]">
        <el-input v-model="form.email"></el-input>
      </el-form-item>
      <el-form-item label="First Name" prop="firstName" :rules="[{
        required: true,
        message: 'Please input First Name', trigger: 'blur'
      }]">
        <el-input v-model="form.firstName"></el-input>
      </el-form-item>
      <el-form-item label="Last Name" prop="lastName" :rules="[{
        required: true,
        message: 'Please input Last Name', trigger: 'blur'
      }]">
        <el-input v-model="form.lastName"></el-input>
      </el-form-item>
      <el-form-item
        label="Role"
        prop="role"
        :rules="[{
        required: true,
        message: 'Please select role', trigger: 'blur'
      }]"
      >
        <el-radio-group v-model="form.role">
          <el-tooltip
            :key="role.id" v-for="role in realmRoles"
            placement="top-start"
            trigger="hover"
            :content="role.description">
            <el-radio-button :label="role.name.toUpperCase()" border></el-radio-button>
          </el-tooltip>
        </el-radio-group>
      </el-form-item>
      <el-form-item v-if="form.role && role !== 'LEVEL_1'" label="Location">
        <el-select v-model="form.locations" multiple placeholder="Select">
          <el-option
            v-for="item in locations"
            :key="item._id"
            :label="item.title"
            :value="item._id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-button @click="saveManager">
        Save
      </el-button>
    </el-form>
  </el-card>
</template>

<script>
import axios from 'axios';
import getRoles from '../utils/roles';
import { AUTH_API_BASE, ADMIN_API_BASE } from '../env';

export default {
  name: 'UserForm',
  props: { facility: { type: String } },
  data() {
    return {
      loading: false,
      realmRoles: [],
      locations: [],
      form: {
        title: '',
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        enabled: true,
        role: '',
        roleName: '',
        roleId: '',
        locations: [],
      },
    };
  },
  async mounted() {
    await this.getRealmRoles();
    await this.getLocations();
  },
  methods: {
    routeToUsers() {
      if (this.facility) {
        this.$router.push({ name: 'Facility Users', params: { facility: this.facility } });
        return;
      }
      this.$router.push({ name: 'Users' });
    },
    async saveManager() {
      this.loading = true;
      try {
        this.$refs.form.validate(async (valid) => {
          const [selectedRole] = this.realmRoles.filter((role) => {
            if (role.name === this.form.role.toLowerCase()) {
              return role;
            }
          });

          this.form.roleId = selectedRole.id;
          this.form.roleName = selectedRole.name;
          this.form.username = this.form.email;

          if (this.form.locations.length === 0 && this.form.role !== 'LEVEL_1') {
            this.loading = false;
            this.$alert('Please select any location.', 'Warning', {
              confirmButtonText: 'Continue Anyway',
              callback: async (action) => {
                if (valid && action === 'confirm') {
                  const success = await this.saveManagerDetails();
                  this.loading = false;
                  if (success) {
                    if (this.facility) {
                      this.$router.push({ name: 'Facility Users', params: { facility: this.facility } });
                      return;
                    }
                    this.$router.push({ name: 'Users' });
                  }
                }
                this.loading = false;
              },
            });
            return;
          }

          if (valid) {
            const success = await this.saveManagerDetails();
            this.loading = false;
            if (success) {
              if (this.facility) {
                this.$router.push({ name: 'Facility Users', params: { facility: this.facility } });
                return;
              }
              this.$router.push({ name: 'Users' });
            }
          }
          this.loading = false;
        });
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async getRealmRoles() {
      const response = await axios.get(`${AUTH_API_BASE}/managers/roles`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      const roles = getRoles();
      this.realmRoles = response.data.filter((role) => {
        if (roles.includes(role.name)) {
          return role;
        }
      });
      this.realmRoles = this.realmRoles.sort((a, b) => a.name.localeCompare(b.name));
    },
    async saveManagerDetails() {
      try {
        await axios.post(`${AUTH_API_BASE}/managers`, {
          ...this.form,
          emailVerified: true,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        return true;
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
        return false;
      }
    },
    async getLocations() {
      const response = await axios.get(`${ADMIN_API_BASE}/facilities`, {
        params: {
          limit: 35,
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.locations = response.data;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/UserForm.vue